import React, {useEffect, useState} from "react"
import Dropdown from "../../../../components/Address/Dropdown"
import Textfield from "../../../../components/Address/Textfield"
import DateField from "../../../../components/DateField/DateField"
import {Formik} from "formik"
import Button from "../../../../components/Button/Button"
import classNames from "classnames"
import moment from "moment"
import {sendWireMessage} from "../../../../resources/wireMessage"
import {useAccessToken} from "../../../../services/auth"
import useAsyncResultIdle from "../../../../hooks/useAsyncResultIdle"
import ServiceMessageResult from "./ServiceMessageResult"
import {
	ServiceMessageFormProps,
	MessageType,
	RequestMessageType,
	WireRequest,
	messageTypes,
	WireResponseAttributes,
} from "./types"
import validationSchema from "./validation"

export default function ServiceMessageForm({banks, incoming = false}: ServiceMessageFormProps) {
	const accessToken = useAccessToken()

	const [wireState, sendWire] = useAsyncResultIdle<any, any, [string, WireRequest, boolean, MessageType]>(
		sendWireMessage
	)

	const [result, setResult] = useState<WireResponseAttributes | null>(null)

	const [error, setError] = useState<string | null>(null)

	const UNUSED_BANK_IDS = new Set([
		"11", // Emprise Bank
		"13", // Vantage Bank
		"14", // Webbank
		"10", // Five Star Bank
		"4", // Piermont Bank
		"1", // BRB Bank
		"2", // Choice Bank
	])

	const filteredBanks = banks.filter((bank) => !UNUSED_BANK_IDS.has(bank.id))

	useEffect(() => {
		if (wireState.isOk()) {
			setResult({
				...wireState.value.data.attributes,
			})
		} else if (wireState.isErr()) {
			setError(JSON.stringify(wireState.error, null, 2))
		}
	}, [wireState])

	const initialValues = {
		messageType: "return-request",
		originalMessageDate: moment().format("YYYY-MM-DD"),
		originalMessageId: "",
		bank: filteredBanks.length > 0 ? filteredBanks[0].id.toString() : "",
		message: "",
	}

	const requestMessageType = (values: typeof initialValues) => {
		if (values.messageType === "investigation-request") {
			return incoming
				? RequestMessageType.InvestigationRequestForIncomingWireMessage
				: RequestMessageType.InvestigationRequestMessage
		}
		if (values.messageType === "return-request") {
			return incoming ? RequestMessageType.ReturnRequestForIncomingWireMessage : RequestMessageType.ReturnRequestMessage
		}
		return RequestMessageType.ReturnRequestMessage
	}

	const processSubmit = (values: typeof initialValues) => {
		setResult(null)
		setError(null)

		const data: WireRequest = {
			type: requestMessageType(values) as RequestMessageType,
			attributes: {
				originalMessageDate: values.originalMessageDate.toString(),
				...(incoming
					? {originalMessageOmad: values.originalMessageId}
					: {originalMessageImad: values.originalMessageId}),
				information: values.message,
				testProductionCode: "P",
			},
			relationships: {
				bank: {
					data: {
						type: "bank",
						id: values.bank,
					},
				},
			},
		}

		sendWire(accessToken, data, incoming, values.messageType as MessageType)
	}

	return (
		<Formik initialValues={initialValues} onSubmit={processSubmit} validationSchema={validationSchema(filteredBanks)}>
			{({values, handleChange, handleBlur, handleSubmit}) => (
				<>
					<form onSubmit={handleSubmit}>
						<div className={classNames("service-message-container")}>
							<div className="form-row two">
								<Dropdown
									name="messageType"
									label="Message Type"
									value={values.messageType}
									onChange={handleChange}
									onBlur={handleBlur}
								>
									{Object.keys(messageTypes).map((t) => (
										<option key={t} value={t}>
											{messageTypes[t as MessageType]}
										</option>
									))}
								</Dropdown>
								<DateField
									name="originalMessageDate"
									label="Original Message Date"
									value={moment(values.originalMessageDate).format("YYYY-MM-DD")}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
							</div>
							<div className="form-row two">
								<Textfield
									placeholder={incoming ? "OMAD" : "IMAD"}
									label="Original Message ID"
									name="originalMessageId"
									value={values.originalMessageId}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
								<Dropdown name="bank" label="Bank" value={values.bank} onChange={handleChange} onBlur={handleBlur}>
									{filteredBanks.map((bank) => (
										<option key={bank.id} value={bank.id.toString()}>
											{bank.attributes.name}
										</option>
									))}
								</Dropdown>
							</div>
							<div className="form-row one">
								<Textfield
									placeholder="Enter Value"
									label="Message"
									name="message"
									value={values.message}
									onChange={handleChange}
									onBlur={handleBlur}
								/>
							</div>
							<div className="form-row service-message-button">
								<Button type="submit">Submit</Button>
							</div>
						</div>
					</form>
					<ServiceMessageResult result={result} error={error} isPending={wireState.isPending()} />
				</>
			)}
		</Formik>
	)
}
